import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Bold, Heading2, List, ListOrdered, MessageSquareQuote, Sparkles } from "lucide-react";

type InstructionEditorProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  name?: string;
  rows?: number;
  placeholder?: string;
};

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, "").trim();
}

export function formatLegacyInstructions(value: string) {
  const clean = stripHtml(value).replace(/[ \t]+/g, " ");
  if (!clean || clean.includes("\n") || /^(?:#{1,3}|[-*]|>|\d+\.)\s/m.test(clean) || clean.length < 120) return clean;
  const sentences = clean.match(/[^.!?…]+(?:[.!?…]+|$)/g)?.map((sentence) => sentence.trim()).filter(Boolean) ?? [];
  return sentences.length > 1 ? sentences.map((sentence) => `- ${sentence}`).join("\n") : clean;
}

function inlineMarkdown(value: string): ReactNode[] {
  const clean = stripHtml(value);
  const expression = /(\*\*([^*]+)\*\*|_([^_]+)_|\*([^*]+)\*)/g;
  const nodes: ReactNode[] = [];
  let cursor = 0;
  for (const match of clean.matchAll(expression)) {
    const index = match.index ?? 0;
    if (index > cursor) nodes.push(clean.slice(cursor, index));
    if (match[2]) nodes.push(<strong key={`${index}-strong`}>{match[2]}</strong>);
    else nodes.push(<em key={`${index}-em`}>{match[3] ?? match[4]}</em>);
    cursor = index + match[0].length;
  }
  if (cursor < clean.length) nodes.push(clean.slice(cursor));
  return nodes;
}

export function FormattedInstructions({ value, compact = false, className = "" }: { value?: string; compact?: boolean; className?: string }) {
  const lines = formatLegacyInstructions(value ?? "").split(/\r?\n/);
  const blocks: ReactNode[] = [];
  for (let index = 0; index < lines.length;) {
    const line = lines[index].trimEnd();
    if (!line.trim()) { index += 1; continue; }
    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const Tag = `h${Math.min(5, heading[1].length + 2)}` as "h3" | "h4" | "h5";
      blocks.push(<Tag key={index}>{inlineMarkdown(heading[2])}</Tag>);
      index += 1;
      continue;
    }
    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index].trim())) quote.push(lines[index++].trim().replace(/^>\s?/, ""));
      blocks.push(<blockquote key={index}>{quote.map((entry, item) => <p key={item}>{inlineMarkdown(entry)}</p>)}</blockquote>);
      continue;
    }
    if (/^[-*]\s+/.test(line.trim())) {
      const items: string[] = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) items.push(lines[index++].trim().replace(/^[-*]\s+/, ""));
      blocks.push(<ul key={index}>{items.map((entry, item) => <li key={item}>{inlineMarkdown(entry)}</li>)}</ul>);
      continue;
    }
    if (/^\d+\.\s+/.test(line.trim())) {
      const items: string[] = [];
      while (index < lines.length && /^\d+\.\s+/.test(lines[index].trim())) items.push(lines[index++].trim().replace(/^\d+\.\s+/, ""));
      blocks.push(<ol key={index}>{items.map((entry, item) => <li key={item}>{inlineMarkdown(entry)}</li>)}</ol>);
      continue;
    }
    const paragraph = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !/^(?:#{1,3}\s+|>\s?|[-*]\s+|\d+\.\s+)/.test(lines[index].trim())) paragraph.push(lines[index++].trim());
    blocks.push(<p key={index}>{inlineMarkdown(paragraph.join(" "))}</p>);
  }
  if (!blocks.length) return null;
  return <div className={`formatted-instructions${compact ? " compact" : ""}${className ? ` ${className}` : ""}`}>{blocks}</div>;
}

export function InstructionEditor({ label, value, onChange, name, rows = 7, placeholder }: InstructionEditorProps) {
  const [preview, setPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const insert = (prefix: string, suffix = "", fallback = "") => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? value.length;
    const end = textarea?.selectionEnd ?? value.length;
    const selected = value.slice(start, end) || fallback;
    const next = `${value.slice(0, start)}${prefix}${selected}${suffix}${value.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      const cursor = start + prefix.length + selected.length + suffix.length;
      textareaRef.current?.setSelectionRange(cursor, cursor);
    });
  };
  const appendBlock = (syntax: string) => onChange(`${value.trimEnd()}${value.trim() ? "\n\n" : ""}${syntax}`);
  const continueList = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter") return;
    const cursor = event.currentTarget.selectionStart;
    const currentLine = value.slice(0, cursor).split("\n").at(-1) ?? "";
    const marker = currentLine.match(/^(\s*)([-*]|(\d+)\.)\s+\S/);
    if (!marker) return;
    event.preventDefault();
    const nextMarker = marker[3] ? `${Number(marker[3]) + 1}. ` : `${marker[2]} `;
    const insertion = `\n${marker[1]}${nextMarker}`;
    onChange(`${value.slice(0, cursor)}${insertion}${value.slice(cursor)}`);
    requestAnimationFrame(() => textareaRef.current?.setSelectionRange(cursor + insertion.length, cursor + insertion.length));
  };
  const structured = formatLegacyInstructions(value);

  return <section className="instruction-editor">
    <header><div><strong>{label}</strong><small>Markdown sécurisé</small></div><div className="instruction-view-toggle"><button type="button" className={!preview ? "active" : ""} onClick={() => setPreview(false)}>Écrire</button><button type="button" className={preview ? "active" : ""} onClick={() => setPreview(true)}>Aperçu</button></div></header>
    {preview ? <div className="instruction-preview">{value.trim() ? <FormattedInstructions value={value} /> : <p>Aucune consigne à prévisualiser.</p>}</div> : <>
      <div className="instruction-toolbar" aria-label={`Mise en forme — ${label}`}>
        <button type="button" onClick={() => appendBlock("## Nouvelle section\n")}><Heading2/> Section</button>
        <button type="button" onClick={() => appendBlock("- ")}><List/> Liste</button>
        <button type="button" onClick={() => appendBlock("1. ")}><ListOrdered/> Étapes</button>
        <button type="button" onClick={() => insert("**", "**", "important")}><Bold/> Gras</button>
        <button type="button" onClick={() => appendBlock("> Conseil : ")}><MessageSquareQuote/> Conseil</button>
        {structured !== stripHtml(value) && <button type="button" onClick={() => onChange(structured)}><Sparkles/> Structurer</button>}
      </div>
      <textarea ref={textareaRef} name={name} rows={rows} maxLength={5000} value={value} onKeyDown={continueList} onChange={(event) => onChange(event.target.value)} placeholder={placeholder ?? "## Technique\n\n1. Placez-vous correctement.\n2. Gardez un mouvement contrôlé.\n\n> Conseil : privilégiez la qualité."}/>
    </>}
    <p className="instruction-help">Titres, listes, étapes, gras, italique et conseils sont acceptés. Le HTML est retiré.</p>
  </section>;
}
